'use client';

import { TrendingUp, Info } from 'lucide-react';
import { useState } from 'react';
import {
  formatEPSSScore,
  formatEPSSPercentile,
  getEPSSBgColor,
  getEPSSSeverity,
} from '../utils/epssApi';

interface EPSSBadgeProps {
  epss: {
    score: number;
    percentile: number;
    date: string;
  };
  variant?: 'inline' | 'full' | 'compact';
  className?: string;
}

export default function EPSSBadge({ epss, variant = 'inline', className = '' }: EPSSBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded ${getEPSSBgColor(epss.score)} ${className}`}
        title={`EPSS: ${formatEPSSScore(epss.score)} - ${formatEPSSPercentile(epss.percentile)}`}
      >
        <TrendingUp className="w-3 h-3" />
        {formatEPSSScore(epss.score)}
      </span>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className={`px-2 py-1 rounded text-sm font-medium ${getEPSSBgColor(epss.score)}`}>
          EPSS: {formatEPSSScore(epss.score)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatEPSSPercentile(epss.percentile)}
        </span>
      </div>
    );
  }

  // Full variant - detailed display with explanation
  return (
    <div className={`p-4 rounded-lg border ${getEPSSSeverityBorder(epss.score)} ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${getEPSSBgColor(epss.score)}`}>
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold">{formatEPSSScore(epss.score)}</span>
              <span className={`text-sm font-medium ${getEPSSTextColor(epss.score)}`}>
                {getEPSSSeverity(epss.score)} Exploitation Risk
              </span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {formatEPSSPercentile(epss.percentile)}
            </div>
          </div>
        </div>

        <button
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 relative"
          onClick={() => setShowTooltip(!showTooltip)}
          onBlur={() => setShowTooltip(false)}
          aria-label="EPSS information"
        >
          <Info className="w-5 h-5" />
          {showTooltip && (
            <div className="absolute right-0 top-8 w-72 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 text-left">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                <strong>EPSS</strong> (Exploit Prediction Scoring System) estimates the probability
                that a vulnerability will be exploited in the wild within the next 30 days.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Data from FIRST.org, updated: {new Date(epss.date).toLocaleDateString()}
              </p>
            </div>
          )}
        </button>
      </div>

      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        This vulnerability has a <strong>{formatEPSSScore(epss.score)}</strong> probability of being
        exploited in the next 30 days, ranking higher than{' '}
        <strong>{Math.round(epss.percentile * 100)}%</strong> of all scored CVEs.
      </p>
    </div>
  );
}

function getEPSSSeverityBorder(score: number): string {
  if (score >= 0.7) return 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/10';
  if (score >= 0.3) return 'border-yellow-300 dark:border-yellow-700 bg-yellow-50/50 dark:bg-yellow-900/10';
  return 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10';
}

function getEPSSTextColor(score: number): string {
  if (score >= 0.7) return 'text-red-600 dark:text-red-400';
  if (score >= 0.3) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}
